import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X } from 'lucide-react';
import { useT } from '../../utils/translate';

const initials = (name = '') => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

// Небольшие аватары ответственных (только отображение): «АБ ВГ +2»
export function AssigneeAvatars({ users = [], max = 3, showNames = false, className = '' }) {
  const list = users.filter(Boolean);
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}>
      <span className="flex -space-x-1.5 shrink-0">
        {shown.map((u, i) => (
          <span key={u._id || i} title={u.name}
            className="w-5 h-5 rounded-full bg-primary-100 ring-2 ring-white flex items-center justify-center text-xs font-bold text-primary-700">
            {initials(u.name)}
          </span>
        ))}
        {rest > 0 && (
          <span className="w-5 h-5 rounded-full bg-surface-200 ring-2 ring-white flex items-center justify-center text-xs font-bold text-ink-secondary">+{rest}</span>
        )}
      </span>
      {showNames && <span className="text-xs text-ink-tertiary truncate">{list.map(u => u.name).join(', ')}</span>}
    </span>
  );
}

// Выбор одного или нескольких ответственных.
// value: массив id (первый — основной). users: [{ _id, name }]
export default function AssigneePicker({ value = [], onChange, users = [], disabled = false, placeholder, className = '' }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Список рисуем поверх страницы (портал), чтобы его не обрезали карточки с overflow-hidden
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const up = below < 260 && r.top > below;
      setPos({ left: r.left, width: r.width, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const k = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open]);

  const ids = value.map(String);
  const selected = ids.map(id => users.find(u => String(u._id) === id)).filter(Boolean);
  const toggle = (id) => {
    const sid = String(id);
    onChange(ids.includes(sid) ? ids.filter(x => x !== sid) : [...ids, sid]);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" disabled={disabled} onClick={() => setOpen(v => !v)}
        className="input w-full flex items-center gap-2 text-left min-h-[38px] disabled:opacity-60">
        {selected.length ? (
          <span className="flex-1 flex items-center gap-2 min-w-0">
            <AssigneeAvatars users={selected} max={3} />
            <span className="truncate text-sm text-ink">{selected.map(u => u.name).join(', ')}</span>
          </span>
        ) : (
          <span className="flex-1 text-sm text-ink-tertiary">{placeholder || t('assignees.none')}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-ink-tertiary shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} style={{ position: 'fixed', zIndex: 80, ...pos }} className="bg-white border border-surface-200 rounded-xl shadow-modal overflow-hidden">
          <div className="px-3 py-2 text-xs text-ink-tertiary border-b border-surface-100 flex items-center justify-between">
            <span>{t('assignees.hint')}</span>
            {selected.length > 0 && (
              <button type="button" onClick={() => onChange([])} className="inline-flex items-center gap-1 text-ink-secondary hover:text-ink">
                <X className="w-3 h-3" />{t('assignees.clear')}
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto">
            {users.map(u => {
              const on = ids.includes(String(u._id));
              const primary = on && ids[0] === String(u._id);
              return (
                <button key={u._id} type="button" onClick={() => toggle(u._id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${on ? 'bg-primary-50 text-primary-700' : 'hover:bg-surface-50 text-ink'}`}>
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-primary-600 border-primary-600' : 'border-surface-300 bg-white'}`}>
                    {on && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 shrink-0">{initials(u.name)}</span>
                  <span className="flex-1 truncate">{u.name}</span>
                  {primary && ids.length > 1 && <span className="text-xs font-semibold text-primary-600 bg-white/70 px-1.5 py-0.5 rounded-full">{t('assignees.primary')}</span>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
